import React from "react";
import { Box } from "@mui/joy";
import type { Training } from "../../../../api/training/Training.typed";
import type { ZekayCustomer } from "../../../../api/customers/Customer.typed";
import TrainingsEmptyState from "../../common/TrainingsEmptyState";
import { TRAININGS_LIST_SELECT_FORMATION_MESSAGE } from "../../common/trainingsUiCopy";
import { useTrainingRowHighlight } from "../../common/hooks/useTrainingRowHighlight";
import TrainingItem from "./TrainingItem";

interface TrainingsListTableProps {
  isLoading: boolean;
  trainings: Training[] | undefined;
  templateId: string;
  customersMap?: Record<number, ZekayCustomer>;
  status: number;
  setTrainings: React.Dispatch<React.SetStateAction<Training[] | undefined>>;
  updateTrainingLocally: (training: Training) => void;
  removeTrainingLocally: (customerID: number) => void;
  refreshTrainings?: () => Promise<Training[] | undefined>;
  highlightTrainingId?: number;
  highlightCustomerId?: number;
}

const TrainingsListTable: React.FC<TrainingsListTableProps> = ({
  isLoading,
  trainings,
  templateId,
  customersMap = {},
  status,
  setTrainings,
  updateTrainingLocally,
  removeTrainingLocally,
  refreshTrainings,
  highlightTrainingId,
  highlightCustomerId,
}) => {
  const emptyMessage =
    templateId.trim() !== "" ? "Aucun résultat" : TRAININGS_LIST_SELECT_FORMATION_MESSAGE;

  const { highlightRowRef, matchesTarget, flashActive } = useTrainingRowHighlight({
    trainings,
    highlightTrainingId,
    highlightCustomerId,
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        width: "100%",
      }}
    >
      {trainings && trainings.length > 0
        ? trainings.map((training, index) => {
            const isTarget = matchesTarget(training);

            return (
              <Box key={training.CustomerID ?? index} ref={isTarget ? highlightRowRef : undefined}>
                <TrainingItem
                  training={training}
                  initialCustomer={
                    training.CustomerID != null ? customersMap[training.CustomerID] : undefined
                  }
                  setTrainings={setTrainings}
                  updateTrainingLocally={updateTrainingLocally}
                  removeTrainingLocally={removeTrainingLocally}
                  refreshTrainings={refreshTrainings}
                  status={status}
                  highlighted={isTarget && flashActive}
                />
              </Box>
            );
          })
        : !isLoading && <TrainingsEmptyState message={emptyMessage} />}
    </Box>
  );
};

export default React.memo(TrainingsListTable);
